/**
 * Telemedicine · Phase 1 · Lobby — three-state control pill.
 *
 * Replicates the Figma "Mic / Câmera / Girar" toggle pills exactly. Icons are
 * inline SVGs traced from the Figma source (nodes 6688:20374 / 20494 / 20384)
 * rather than Ionicons substitutes — the design uses a thin-stroke microphone
 * with the U-arc, a record-video glyph with a centered lens dot, and a
 * recycle-style camera-flip arrow that none of the icon sets match cleanly.
 *
 *   variant 'mic'     · on  → cream pill with microphone + 5-bar waveform
 *                     · off → dune pill with microphone + diagonal orange slash
 *   variant 'camera'  · on  → cream pill with record-video + small blue dot
 *                     · off → dune pill with record-video + diagonal orange slash
 *   variant 'flip'    · always cream with camera-flip icon (no on/off slash)
 *
 * Each pill is `flex: 1` so a row of three stretches edge-to-edge under the
 * preview. The label sits below the pill, not inside it (per Figma).
 */
import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import Svg, { Line, Path } from 'react-native-svg';
import * as Haptics from 'expo-haptics';
import AnimatedSoundwave from '@/components/video/AnimatedSoundwave';

export type PillVariant = 'mic' | 'camera' | 'flip';

interface LobbyControlPillProps {
  variant: PillVariant;
  /** For mic/camera. 'flip' ignores this. */
  on?: boolean;
  label: string;
  onPress: () => void;
  disabled?: boolean;
  /**
   * Live mic level 0..1 — drives the animated soundwave on the mic pill.
   * Ignored for non-mic variants. Defaults to 0 (idle bars).
   */
  audioLevel?: number;
}

const CREAM_PILL = '#F0EEE4';
const DUNE_PILL = '#353535';
const INK = '#151515';
const WHITE = '#FFFFFF';
const BLUE_HORIZON = '#669AB7';
const ORANGE_TENN = '#DE5609';

export default function LobbyControlPill({
  variant,
  on = true,
  label,
  onPress,
  disabled,
  audioLevel = 0,
}: LobbyControlPillProps) {
  // Wrap the consumer's onPress so every tap fires a light haptic. Mic /
  // camera toggles use a "selection" tick (subtle, repeatable, doesn't
  // compete with the visual state change), while flip uses a light impact —
  // it's a one-shot action without a sticky on/off state. Errors are
  // swallowed because expo-haptics is best-effort on web and older devices.
  const handlePress = React.useCallback(() => {
    try {
      if (variant === 'flip') {
        Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
      } else {
        Haptics.selectionAsync();
      }
    } catch {}
    onPress();
  }, [onPress, variant]);

  const isFlip = variant === 'flip';
  const showOff = !isFlip && !on;
  const pillBg = showOff ? DUNE_PILL : CREAM_PILL;
  const iconColor = showOff ? WHITE : INK;

  return (
    <View style={styles.col}>
      <TouchableOpacity
        style={[
          styles.pill,
          { backgroundColor: pillBg },
          disabled && { opacity: 0.5 },
        ]}
        onPress={handlePress}
        disabled={disabled}
        activeOpacity={0.85}
        accessibilityRole="button"
        accessibilityState={{ disabled, selected: !showOff }}
        accessibilityLabel={`${label}${isFlip ? '' : on ? ' ligado' : ' desligado'}`}
      >
        <View style={styles.iconStack}>
          {variant === 'mic' && <MicGlyph color={iconColor} />}
          {variant === 'camera' && <RecordVideoGlyph color={iconColor} size={24} />}
          {variant === 'flip' && <CameraFlipGlyph color={iconColor} />}
          {/* Diagonal orange slash for off state, drawn via SVG so the angle
              stays crisp at any density. */}
          {showOff && <DiagonalSlash />}
        </View>

        {variant === 'mic' && on && <AnimatedSoundwave level={audioLevel} />}
        {variant === 'camera' && on && <View style={styles.recordDot} />}
      </TouchableOpacity>
      <Text style={styles.label}>{label}</Text>
    </View>
  );
}

/* ---------- Figma-accurate icon glyphs ---------- */

function MicGlyph({ color }: { color: string }) {
  // Figma node 6688:20374 — 24×24, stroke 2.
  return (
    <Svg width={24} height={24} viewBox="0 0 24 24" fill="none">
      <Path
        d="M12 21V18.15"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <Path
        d="M19.21 10.91C19.22 14.9 15.99 18.14 12 18.15C8.01 18.14 4.78 14.9 4.79 10.91"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <Path
        d="M12 14.56C9.97 14.55 8.33 12.91 8.34 10.88V6.66C8.34 4.64 9.98 3 12 3C14.02 3 15.66 4.64 15.66 6.66V10.88C15.66 12.91 14.03 14.55 12 14.56Z"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

function RecordVideoGlyph({
  color,
  size = 24,
  filled = false,
}: {
  color: string;
  size?: number;
  /**
   * When true, paints the camera body white and the speaker triangle in
   * `color` (matching Figma node 6688:20549 — the cream badge on the lobby
   * headline). When false, renders outline-only — used inside the on/off
   * toggle pills where the pill bg supplies the contrast.
   */
  filled?: boolean;
}) {
  // Figma node 6688:20494 — record-video (rounded body + triangle lens + center dot).
  // Geometry uses the 20-viewBox coords from the Figma export so both the
  // 20×20 badge and 24×24 pill icon trace from the same source.
  return (
    <Svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <Path
        d="M5.1411 4.42627H10.3645C12.1899 4.42627 13.3268 5.71529 13.3268 7.53904V12.4606C13.3268 14.2843 12.1899 15.5733 10.3637 15.5733H5.1411C3.31486 15.5733 2.17969 14.2843 2.17969 12.4606V7.53904C2.17969 5.71529 3.32068 4.42627 5.1411 4.42627Z"
        fill={filled ? '#FFFFFF' : 'none'}
        stroke={color}
        strokeWidth={1.846}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <Path
        d="M13.3203 8.45459L15.9407 6.30983C16.3649 5.96221 16.9911 6.02459 17.3387 6.44872C17.4842 6.62668 17.5641 6.84956 17.5632 7.07909L17.5541 12.9262C17.5524 13.4751 17.1075 13.9192 16.5586 13.9175C16.3299 13.9175 16.1079 13.8377 15.9308 13.6921L13.3203 11.5482"
        fill={filled ? color : 'none'}
        stroke={color}
        strokeWidth={1.846}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <Path
        d="M5.83984 10.0016C5.83984 11.0586 6.69641 11.9143 7.75261 11.9143C8.80954 11.9143 9.66615 11.0586 9.66615 10.0016C9.66615 8.94462 8.80954 8.08887 7.75261 8.08887C6.69641 8.08887 5.83984 8.94462 5.83984 10.0016Z"
        stroke={color}
        strokeWidth={1.846}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

function CameraFlipGlyph({ color }: { color: string }) {
  // Figma node 6688:20384 — camera body with two arcing flip arrows inside.
  return (
    <Svg width={24} height={24} viewBox="0 0 24 24" fill="none">
      <Path
        d="M12.52 11.56L14.05 10.79C13.5 10.3 12.78 10.01 12 10.01C10.29 10.01 8.91 11.4 8.91 13.1"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <Path
        d="M11.48 14.65L9.95 15.43C10.5 15.91 11.22 16.2 12 16.2C13.71 16.2 15.09 14.81 15.09 13.11"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <Path
        d="M12 19.86H7.11C4.84 19.86 3 18.02 3 15.76V10.11C3 8.42 4.37 7.06 6.06 7.06C6.55 7.04 7.02 6.83 7.36 6.47C7.54 6.27 7.83 5.77 8.1 5.29C8.49 4.58 9.24 4.14 10.05 4.14H13.97C14.79 4.14 15.55 4.59 15.94 5.32C16.19 5.79 16.46 6.27 16.64 6.47C16.98 6.82 17.45 7.03 17.94 7.05C19.63 7.05 21 8.42 21 10.11V15.76C21 18.02 19.16 19.86 16.89 19.86H15.15"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

function DiagonalSlash() {
  // 24×24 SVG matching the Tenn-orange stroke from Figma. Drawn slightly
  // outside the icon's bbox (margin) to read as "crossed out".
  return (
    <Svg
      width={26}
      height={26}
      viewBox="0 0 24 24"
      style={styles.slash}
      pointerEvents="none"
    >
      <Line
        x1={3}
        y1={21}
        x2={21}
        y2={3}
        stroke={ORANGE_TENN}
        strokeWidth={2}
        strokeLinecap="round"
      />
    </Svg>
  );
}

/** Re-exported so other surfaces (lobby badge, permissions screen, in-call
 *  controls bar, post-call) can render the exact same Figma glyphs without
 *  re-tracing them. `DiagonalSlash` is shared too — same Tenn-orange
 *  cross-out used by every "off" state across the call surfaces. */
export { MicGlyph, RecordVideoGlyph, CameraFlipGlyph, DiagonalSlash };

const styles = StyleSheet.create({
  col: {
    flex: 1,
    alignItems: 'center',
    gap: 8,
  },
  pill: {
    width: '100%',
    height: 48,
    borderRadius: 32,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 8,
    overflow: 'hidden',
  },
  iconStack: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  slash: {
    position: 'absolute',
    top: -1,
    left: -1,
  },
  recordDot: {
    width: 6,
    height: 6,
    borderRadius: 4,
    backgroundColor: BLUE_HORIZON,
  },
  label: {
    color: INK,
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 19.6,
    textAlign: 'center',
  },
});
